import { useState } from "react"
import { z } from "zod"

import { pro } from "@productlane/lib"
import {
	Alert,
	Form,
	FormInput,
	FormSelect,
	FormSwitch,
	SubmitButton,
	useZodForm,
} from "@productlane/ui"

import { toast } from "@/components/toast"
import { UpgradeButton } from "@/components/upgrade-button"
import {
	EMAIL_MAXIMUM_LENGTH,
	EMAIL_MAXIMUM_LENGTH_ERROR_MESSAGE,
} from "@/constants"
import { api } from "@/lib/api"
import { useRequiredPlans } from "@/lib/plans"
import { useSession } from "@/lib/session"
import { useTrial } from "@/lib/useTrial"
import { PlanPill } from "@/screens/settings/plan-pill"
import { SettingsContainer } from "@/screens/settings/settings-switches/settings-container"
import { SettingsHeader } from "../../settings-header"
import { ImportPortalBadge, useImportPortalId } from "../import-portal"

const DAYS_MAX = 1826 // 5 years

const ProductlaneStatusOptions = [
	{ label: "New", value: "new" },
	{ label: "Done", value: "done" },
	{ label: "Close loop", value: "close-loop" },
]

const FormSchema = z.object({
	zendeskUrl: z
		.string()
		.url("Invalid URL.")
		.min(1)
		.refine((url) => url.startsWith("https://") && url.endsWith("/api/v2/"), {
			message:
				"URL must start with https:// and end with /api/v2/, e.g. https://example.zendesk.com/api/v2/",
		}),
	zendeskToken: z.string().min(5, {
		message: "Zendesk token is too short. Please check and try again.",
	}),
	zendeskEmail: z
		.string()
		.email()
		.max(EMAIL_MAXIMUM_LENGTH, EMAIL_MAXIMUM_LENGTH_ERROR_MESSAGE),
	importAttachments: z.boolean(),
	daysBack: z.coerce
		.number()
		.min(0, "Days must be 0 or more.")
		.max(1826, `Maximum allowed is ${DAYS_MAX} days (5 years).`),
	newStatus: z.string(),
	openStatus: z.string(),
	closedStatus: z.string(),
	solvedStatus: z.string(),
})

export function ZendeskImportPage() {
	const { session } = useSession()
	const { hasTrialEnded } = useTrial()

	const form = useZodForm({
		schema: FormSchema,
		defaultValues: {
			zendeskEmail: session?.email,
			importAttachments: false,
			daysBack: 0,
			newStatus: "new",
			openStatus: "new",
			closedStatus: "done",
			solvedStatus: "close-loop",
		},
	})

	const portalInstanceId = useImportPortalId()
	const [step, setStep] = useState<
		"validate" | "options" | "success" | "failure"
	>("validate")
	const [error, setError] = useState("")
	const { passes: hasRequiredPlan } = useRequiredPlans(pro.andUp)

	const executeImportMutation = api.zendesk.executeImport.useMutation({
		onSuccess: () => {
			toast.success({
				title: "Imported data from Zendesk successfully!",
			})
			setStep("success")
			form.reset()
		},
		onError: (e) => {
			toast.error({
				title:
					"Something went wrong during the data import. Please try again later.",
			})
			setStep("failure")
			setError(e.message)
			form.clearErrors()
			form.reset()
		},
	})

	const validateDataMutation = api.zendesk.validateData.useMutation({
		onSuccess: () => {
			setStep("options")
			toast.success({ title: "Your credentials are valid." })
		},
		onError: () => {
			toast.error({
				title: "Could not connect to Zendesk. Please check your credentials.",
			})
			form.clearErrors()
			form.reset(form.getValues())
		},
	})

	return (
		<>
			<SettingsHeader
				title="Zendesk import"
				subtitle={null}
				titleSuffix={
					<>
						<PlanPill plan="pro" />
						<ImportPortalBadge />
					</>
				}
			/>
			{!hasTrialEnded ? (
				<>
					{(step === "validate" || step === "options") && (
						<SettingsContainer>
							<div className="text-primary">
								Import your Zendesk data to Productlane
							</div>
							{step === "validate" && (
								<div className="pt-2 text-sm text-secondary">
									To get started with the Zendesk import,{" "}
									<a
										href="https://support.zendesk.com/hc/en-us/articles/4408889192858-Managing-access-to-the-Zendesk-API"
										target="_blank"
										rel="noreferrer"
										className="font-semibold underline"
									>
										please create Zendesk token
									</a>
									. We won&apos;t store this token or any data needed for import
									in our system and use it only for the import process.
								</div>
							)}

							{step === "options" && (
								<div className="mt-1 text-sm text-secondary">
									Migration may take a while, so it will run in the background.
									<br />
									Note: We will migrate tickets, contacts, tags and attachments
									from Zendesk.
								</div>
							)}

							<Form
								className="flex flex-col gap-2 pt-4"
								form={form}
								onSubmit={async (values) => {
									if (!hasRequiredPlan) {
										toast.error({ title: "Pro plan or higher required" })
										return
									}
									if (step === "validate") {
										await validateDataMutation.mutateAsync({
											...values,
										})
										return
									}
									await executeImportMutation.mutateAsync({
										...values,
										portalInstanceId,
									})
								}}
							>
								<FormInput
									name="zendeskUrl"
									placeholder="https://example.zendesk.com/api/v2/"
									label="API Url"
									disabled={step === "options"}
								/>

								<FormInput
									type="password"
									name="zendeskToken"
									placeholder="EF9aRCivf..."
									label="Zendesk token"
									disabled={step === "options"}
								/>

								<FormInput
									type="email"
									name="zendeskEmail"
									placeholder="example@gmail.com"
									label="Your Zendesk email"
									disabled={step === "options"}
								/>

								{step === "options" && (
									<div className="my-2 space-y-2">
										<div className="flex flex-row items-center justify-between gap-8">
											<div>
												<div className="text-sm font-medium text-primary">
													Days back
												</div>
												<div className="mt-1 text-left text-sm font-normal text-secondary">
													Enter how many days to sync from today. Use 0 to sync
													all Zendesk data.
												</div>
											</div>
											<FormInput
												name="daysBack"
												type="number"
												className="max-w-24"
											/>
										</div>
										<FormSwitch
											disabled={executeImportMutation.isLoading}
											name="importAttachments"
											label="Import attachments"
											description="Include file attachments from Zendesk tickets and comments. This may take longer to import."
										/>

										<div className="mt-2">
											<div className="mb-3 text-sm font-medium">
												Map Zendesk ticket statuses
											</div>
											<div className="flex flex-col gap-2">
												<div className="itemms-center flex flex-row justify-between">
													<div className="text-sm text-secondary">New</div>
													<FormSelect
														name="newStatus"
														options={ProductlaneStatusOptions}
													/>
												</div>

												<div className="itemms-center flex flex-row justify-between">
													<div className="text-sm text-secondary">Open</div>
													<FormSelect
														name="openStatus"
														options={ProductlaneStatusOptions}
													/>
												</div>

												<div className="itemms-center flex flex-row justify-between">
													<div className="text-sm text-secondary">Closed</div>
													<FormSelect
														name="closedStatus"
														options={ProductlaneStatusOptions}
													/>
												</div>

												<div className="itemms-center flex flex-row justify-between">
													<div className="text-sm text-secondary">Solved</div>
													<FormSelect
														name="solvedStatus"
														options={ProductlaneStatusOptions}
													/>
												</div>
											</div>
										</div>
									</div>
								)}

								<div className="flex items-center justify-end gap-4 pt-4">
									{hasRequiredPlan ? (
										<SubmitButton>
											{step === "validate"
												? "Validate credentials"
												: "Import data"}
										</SubmitButton>
									) : (
										<UpgradeButton required={pro.andUp} />
									)}
								</div>
							</Form>
						</SettingsContainer>
					)}

					{step === "success" && (
						<SettingsContainer>
							<p className="text-primary">
								Your Zendesk import has been queued and is running in the
								background 🚚
							</p>
							<p className="mt-2 text-sm text-secondary">
								You&apos;ll be notified once the import is complete. This may
								take a little time depending on the amount of data. Some data
								might not appear immediately due to caching. <br />
								<br />
								Please allow a little time for everything to show up.
							</p>
						</SettingsContainer>
					)}

					{step === "failure" && (
						<SettingsContainer>
							<p className="text-secondary">
								Your credentials were correct, but something went wrong during
								the import process. This might be due to unexpected data or a
								temporary issue on Zendesk&apos;s side. Please try again or
								contact support if the issue persists.
							</p>
							{error && (
								<SettingsContainer className="relative mt-8 max-w-[calc(100vw-2rem)] overflow-hidden md:max-w-[calc(100vw-18rem)]">
									<pre className="overflow-scroll rounded-md p-2 text-sm md:text-sm">
										{error}
									</pre>
								</SettingsContainer>
							)}
						</SettingsContainer>
					)}
				</>
			) : (
				<Alert variant="warning">Trial ended. Upgrade to import data.</Alert>
			)}
		</>
	)
}
